import React, { memo, useCallback, useEffect, useRef } from 'react'
import Font from '@sport1/news-styleguide/Font'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { RenderListItemProps } from '@sport1/news-styleguide/Types'
import { MatchFactsElementModel, MatchFactsProps } from '@sport1/types/web'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import { FlatListRefType } from '@/components/HorizontalSlider/types'
import { useTracking } from '@/utils/tracking/TrackingProvider'
import { sport1Regular } from '@/helpers/fonts'
import HorizontalSlider from '@/components/HorizontalSlider'

export const CARD_WIDTH = 311
export const SPACE_BETWEEN_CARDS = 16

const keyExtractor = (value: MatchFactsElementModel, index: number): string => {
    return `match-${value.matchFactsClientModel.matchId}-fact-${index}`
}

const renderItem = ({ item }: RenderListItemProps<MatchFactsElementModel>) => (
    <NonFlexingContainer
        backgroundColor="white"
        height="100%"
        width={CARD_WIDTH}
        paddingTop="spacing-7"
        paddingX="spacing-7"
        paddingBottom="spacing-4"
        border={['0px', '0px', '1px']}
    >
        <Font
            nextFontFamilyVariant={sport1Regular.style.fontFamily}
            fontSize="font-size-5"
            lineHeight="lineHeight-24"
        >
            {item.matchFactsClientModel.fact}
        </Font>
    </NonFlexingContainer>
)

type Props = Pick<MatchFactsProps, 'content' | 'headline'>

const MatchFacts = ({ content, headline }: Props) => {
    const outerRef = useRef<HTMLDivElement>(null)
    const offsetLeft = React.useRef(0)
    const scrollViewRef = React.useRef<FlatListRefType | null>(null)
    const [maxScrollOffset, setMaxScrollOffset] = React.useState(0)
    const { trackInteraction } = useTracking()

    const onScroll = React.useCallback(({ scrollLeft = 0 }: { scrollLeft?: number }): void => {
        if (offsetLeft.current !== scrollLeft) {
            offsetLeft.current = scrollLeft
        }
    }, [])

    const scrollTo = useCallback(
        (offsetValue: number, maxScrollOffset: number) => {
            let newOffset
            if (offsetValue >= 0) {
                const cardIndex = Math.round(
                    offsetLeft.current / (CARD_WIDTH + SPACE_BETWEEN_CARDS)
                )
                newOffset = (cardIndex + 1) * offsetValue
            } else {
                const cardIndex = Math.ceil(offsetLeft.current / (CARD_WIDTH + SPACE_BETWEEN_CARDS))
                newOffset = (cardIndex - 1) * (CARD_WIDTH + SPACE_BETWEEN_CARDS)
            }

            const clampedOffset =
                offsetValue < 0 ? Math.max(newOffset, 0) : Math.min(newOffset, maxScrollOffset)
            scrollViewRef.current?.scrollToOffset({ animated: true, offset: clampedOffset })
            offsetLeft.current = clampedOffset
        },
        [offsetLeft]
    )

    const resize = useCallback(() => {
        const newCurrentWidth = outerRef.current?.offsetWidth as number
        setMaxScrollOffset(
            CARD_WIDTH * content.length +
                SPACE_BETWEEN_CARDS * (content.length - 1) -
                newCurrentWidth
        )
    }, [content.length])

    // go back to start when resizing; easier that way
    useEffect(() => scrollTo(-maxScrollOffset, maxScrollOffset), [scrollTo, maxScrollOffset])

    React.useEffect(() => {
        window.addEventListener('resize', resize)
        resize()
        return () => window.removeEventListener('resize', resize)
    }, [resize])

    const onPressArrowCallback = () => {
        trackInteraction({
            interaction_category: 'liveticker',
            interaction_action: 'slide',
            interaction_label: 'facts',
        })
    }

    return (
        <NonFlexingContainer marginBottom="spacing-9" paddingBottom="spacing-7" innerRef={outerRef}>
            <NonFlexingContainer testID="headline" marginBottom="spacing-7">
                <CardHeadline
                    testID="headline"
                    cardHeadlineType={['cardHeadlineS', 'cardHeadlineS', 'cardHeadlineS']}
                    text={headline}
                />
            </NonFlexingContainer>

            <HorizontalSlider<MatchFactsElementModel>
                items={content}
                backgroundColor="TRANSPARENT"
                onScroll={onScroll}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                spaceX={['standard', 'standard', 'medium']}
                buttonVariant="primary"
                onPressArrow={onPressArrowCallback}
                testID="facts-slider"
            />
        </NonFlexingContainer>
    )
}

export default memo(MatchFacts)
